/**
 * QueryService handles persistent query tracking and daily reset logic for the web version.
 * Uses localStorage for persistence.
 */
export class QueryService {
    private static instance: QueryService;
    private listeners: (() => void)[] = [];
    private isPro: boolean = false;

    private constructor() {
        this.checkReset();
    }

    public static getInstance(): QueryService {
        if (!QueryService.instance) {
            QueryService.instance = new QueryService();
        }
        return QueryService.instance;
    }

    public setProStatus(status: boolean): void {
        this.isPro = status;
        
        const currentRemStr = localStorage.getItem('query_remaining');
        const currentRem = currentRemStr ? parseInt(currentRemStr, 10) : 0;
        const limitType = localStorage.getItem('query_limit_type');
        
        // Upgrade limit if they are pro but currently restricted to 'free' limits
        if (status && limitType !== 'pro') {
            const difference = 1000 - 100;
            localStorage.setItem('query_remaining', (currentRem + difference > 1000 ? 1000 : currentRem + difference).toString());
            localStorage.setItem('query_limit_type', 'pro');
            this.notify();
        } else if (!status && limitType === 'pro') {
            // Downgrade to free limits
            localStorage.setItem('query_remaining', (currentRem > 100 ? 100 : currentRem).toString());
            localStorage.setItem('query_limit_type', 'free');
            this.notify();
        }
    }

    private checkReset(): void {
        const now = new Date();
        const nextResetStr = localStorage.getItem('query_next_reset');
        
        let shouldReset = false;
        if (!nextResetStr) {
            shouldReset = true;
        } else {
            const nextReset = new Date(nextResetStr);
            // If invalid, or past the reset time, or on the wrong schedule (not 3 AM)
            if (isNaN(nextReset.getTime()) || now >= nextReset || nextReset.getHours() !== 3) {
                shouldReset = true;
            }
        }

        if (shouldReset) {
            console.log("[QueryService] Resetting daily query limit (3 AM Reset).");
            const newLimit = this.isPro ? '1000' : '100';
            localStorage.setItem('query_remaining', newLimit);
            localStorage.setItem('query_limit_type', this.isPro ? 'pro' : 'free');
            
            // Calculate next 3 AM
            let nextReset = new Date();
            nextReset.setHours(3, 0, 0, 0);
            if (now >= nextReset) {
                nextReset.setDate(nextReset.getDate() + 1);
            }
            
            localStorage.setItem('query_next_reset', nextReset.toISOString());
            this.notify();
        }
    }

    public getRemaining(): number {
        this.checkReset();
        const rem = localStorage.getItem('query_remaining');
        return rem ? parseInt(rem, 10) : 100;
    }

    public getMaxQueries(): number {
        return this.isPro ? 1000 : 100;
    }

    public getResetTimeStr(): string {
        const now = new Date();
        const nextResetStr = localStorage.getItem('query_next_reset');
        if (!nextResetStr) return "Renews in 24h";

        const nextReset = new Date(nextResetStr);
        if (isNaN(nextReset.getTime())) return "Renews in 24h";

        const diffMs = nextReset.getTime() - now.getTime();
        if (diffMs <= 0) return "Renewing...";

        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 0) {
            return `Renews in ${hours}h ${mins}m`;
        }
        return `Renews in ${mins}m`;
    }

    public useQuery(count: number = 1): boolean {
        const remaining = this.getRemaining();
        if (remaining >= count) {
            localStorage.setItem('query_remaining', (remaining - count).toString());
            this.notify();
            return true;
        }
        return false;
    }

    public subscribe(callback: () => void): () => void {
        this.listeners.push(callback);
        return () => {
            this.listeners = this.listeners.filter(l => l !== callback);
        };
    }

    private notify(): void {
        this.listeners.forEach(l => l());
    }
}

export const queryService = QueryService.getInstance();
